/**
 * AccountFormValues → backend body for `POST /account` / `PUT /account/{id}`.
 *
 * Direct port of `saveAccount()` from the Angular account.component.ts.
 * Invariants preserved (each one is a real Angular branch):
 *
 *   - `accountMode === 'new'`        → only `rmkTemplateAccountName` + `rmkTemplateApiKey` populated
 *   - `accountMode === 'existing'`   → only `rmkExistTemplateAccount` + `rmkExistTemplateApiKey` populated
 *   - `notifyApplicationIds` / `templatifyApplicationIds` are built from the
 *     applications table. When status === 'existsInBoth', cross-match looks up
 *     the OTHER catalog by name — full Notify + Templatify lists are required
 *     for this lookup, so the caller must pass them in.
 *   - `whiteListedIPs` is a `string[]` (flat array of dotted IPs), NOT
 *     `{iPs: string[]}` — Angular sends just the array
 *   - `rateLimitPerMin/Day` zeroed when `!rateLimitApplies`
 *   - `costCenterId` nulled when `!useCostCenter`
 *   - `applicationIds` and `applicationsRequest` include EVERY row,
 *     even rows with empty applicationId (Angular doesn't filter)
 *   - `groups` filtered to rows whose `applicationId` appears in the table
 *   - `users.confirmPassword` stripped per user; whole `users` dropped when `!addUser`
 *   - `id` dropped on create
 */

import type {
  AccountFormValues,
  ApplicationRowOption,
} from '../types/account';

export interface AccountPayloadLookups {
  notifyApps: ApplicationRowOption[];
  templatifyApps: ApplicationRowOption[];
}

export interface AccountPayload {
  id?: string;
  accountName: string;
  directoryId?: string;
  apiKey: string;
  accountStatus: string;
  loggingStatus: string;
  logRetentionDays: number | null;
  providerIds: string[];
  licenseId: string;
  validityTimeFrame: string | null;
  communicationTypes: string[];
  isLicenseBlocked: boolean;
  rateLimitApplies: boolean;
  rateLimitPerMin: number;
  rateLimitPerDay: number;
  whitelistApplies: boolean;
  whiteListedIPs: string[];
  useCostCenter: boolean;
  costCenterId: string | null;
  useTemplates: boolean;
  aiAvailable?: boolean;
  aiProvider?: string;
  aiApiKey?: string;
  aiTranscriptionApiKey?: string;
  useTemplatify: boolean;
  contactCenterEnabled: boolean;
  portalEnabled: boolean;
  automationsEnabled: boolean;
  maxUsers: number | string | null;
  contactCenterReplyToEmail: string;
  autoCreateEnabled: boolean;
  autoCreateGlobal: boolean;
  accountMode: string;
  rmkTemplateAccountName: string;
  rmkTemplateApiKey: string;
  rmkExistTemplateAccount: unknown;
  rmkExistTemplateApiKey: string;
  isUpdateTemplateApiKey: boolean;
  templatifyGroupId: string | null;
  applicationIds: string[];
  notifyApplicationIds: string[];
  templatifyApplicationIds: string[];
  applicationsRequest: Array<{
    applicationId: string;
    applicationName: string;
    syncStatus: string;
  }>;
  groups: Array<{
    applicationId: string;
    groups: Array<{ groupId?: string | null; groupName: string; members: unknown[] }>;
  }>;
  users?: Array<{
    id?: string;
    name: string;
    surname: string;
    email: string;
    password?: string;
    scopes: string[];
  }>;
}

const joinOctets = (o: {
  octet1: string;
  octet2: string;
  octet3: string;
  octet4: string;
}): string => `${o.octet1}.${o.octet2}.${o.octet3}.${o.octet4}`;

const dateStringToIso = (s: string | null): string | null => {
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

export const buildAccountPayload = (
  v: AccountFormValues,
  lookups: AccountPayloadLookups = { notifyApps: [], templatifyApps: [] }
): AccountPayload => {
  // Placeholder rows (no app picked yet) don't travel to the backend.
  const applicationIds = v.applications
    .map((a) => a.applicationId)
    .filter(Boolean);
  const notifyIds: string[] = [];
  const templatifyIds: string[] = [];
  for (const app of v.applications) {
    if (app.notify && app.applicationId) {
      notifyIds.push(app.applicationId);
      if (app.status === 'existsInBoth') {
        const match = lookups.templatifyApps.find((x) => x.name === app.name);
        if (match) templatifyIds.push(match.id);
      }
    }
    if (app.templatify && app.applicationId) {
      templatifyIds.push(app.applicationId);
      if (app.status === 'existsInBoth') {
        const match = lookups.notifyApps.find((x) => x.name === app.name);
        if (match) notifyIds.push(match.id);
      }
    }
  }

  // Groups may hang off apps linked in Account Information (rows) OR in
  // the Templatify Integration multi-select.
  const groupableAppIds = new Set([
    ...applicationIds,
    ...(v.useTemplatify ? v.templatifyExtraApplicationIds ?? [] : []),
  ]);
  const groups = v.applicationGroups.filter((g) =>
    groupableAppIds.has(g.applicationId)
  );

  const whiteListedIPs = v.whitelistApplies
    ? v.whiteListedIps.map(joinOctets)
    : [];

  const payload: AccountPayload = {
    id: v.id,
    accountName: v.accountName,
    // Only sent on create; an existing account is linked through its own endpoint.
    ...(!v.id && v.directoryId ? { directoryId: v.directoryId } : {}),
    apiKey: v.apiKey,
    accountStatus: v.accountStatus || 'active',
    loggingStatus: v.loggingStatus || 'none',
    logRetentionDays:
      v.logRetentionDays === null ||
      (v.logRetentionDays as unknown as string) === '' ||
      Number.isNaN(Number(v.logRetentionDays))
        ? null
        : Number(v.logRetentionDays),
    providerIds: v.providerIds,
    licenseId: v.licenseId,
    validityTimeFrame: dateStringToIso(v.validityTimeFrame),
    communicationTypes: v.communicationTypes,
    // The Blocked-license checkbox is gone from the UI — a blocked account
    // status implies the blocked license.
    isLicenseBlocked: v.accountStatus === 'blocked',
    rateLimitApplies: v.rateLimitApplies,
    rateLimitPerMin: v.rateLimitApplies ? Number(v.rateLimitPerMin) || 0 : 0,
    rateLimitPerDay: v.rateLimitApplies ? Number(v.rateLimitPerDay) || 0 : 0,
    whitelistApplies: v.whitelistApplies,
    whiteListedIPs,
    useCostCenter: v.useCostCenter,
    costCenterId: v.useCostCenter ? v.costCenterId : null,
    // Use-templates checkbox is gone — using Templatify implies templates.
    useTemplates: v.useTemplatify,
    // AI config. OJO con las keys: el backend interpreta cadena vacía como BORRAR
    // (`AiApiKey != null` → vacío ⇒ null) y `undefined` como "no tocar". El formulario
    // carga siempre la key vacía —nunca vuelve del API—, así que enviarla tal cual
    // borraba la key guardada en CUALQUIER edición de la cuenta, dejando la IA muerta
    // sin avisar. Se envía solo si el operador ha escrito una nueva; al apagar la IA sí
    // se manda vacía, que ahí borrar es lo que se quiere.
    aiAvailable: v.aiAvailable,
    aiProvider: v.aiAvailable ? v.aiProvider || undefined : undefined,
    aiApiKey: v.aiAvailable ? v.aiApiKey || undefined : '',
    aiTranscriptionApiKey: v.aiAvailable ? v.aiTranscriptionApiKey || undefined : '',
    useTemplatify: v.useTemplatify,
    contactCenterEnabled: v.contactCenterEnabled,
    portalEnabled: v.portalEnabled,
    automationsEnabled: v.automationsEnabled,
    // Vacio significa "sin tope": se manda 0, que el API guarda como null.
    maxUsers: v.maxUsers === '' || v.maxUsers == null ? 0 : Number(v.maxUsers),
    contactCenterReplyToEmail: v.contactCenterReplyToEmail,
    autoCreateEnabled: v.autoCreateEnabled,
    autoCreateGlobal: v.autoCreateGlobal,
    accountMode: v.accountMode,
    rmkTemplateAccountName:
      v.accountMode === 'new' ? v.rmkTemplateAccountName : '',
    rmkTemplateApiKey: v.accountMode === 'new' ? v.rmkTemplateApiKey : '',
    rmkExistTemplateAccount:
      v.accountMode === 'existing' ? v.rmkExistTemplateAccount : null,
    rmkExistTemplateApiKey:
      v.accountMode === 'existing' ? v.rmkExistTemplateApiKey : '',
    isUpdateTemplateApiKey: v.isUpdateTemplateApiKey,
    templatifyGroupId: v.useTemplatify ? v.templatifyGroupId : null,
    applicationIds,
    notifyApplicationIds: notifyIds,
    // Applications-step rows + the extra multi-select from the
    // Templatify step, deduplicated.
    templatifyApplicationIds: Array.from(
      new Set([
        ...templatifyIds,
        ...(v.useTemplatify ? v.templatifyExtraApplicationIds ?? [] : []),
      ])
    ),
    applicationsRequest: v.applications
      .filter((a) => Boolean(a.applicationId))
      .map((a) => ({
        applicationId: a.applicationId,
        applicationName: a.name,
        // The backend enum rejects an empty string; rows added outside the
        // old Applications-step probe (dropdown picks, legacy edits) fall
        // back to the side they belong to.
        syncStatus:
          a.status || (a.templatify ? 'existsInBoth' : 'notifyOnly'),
      })),
    groups,
  };

  if (v.addUser) {
    payload.users = v.users.map((u) => ({
      id: u.id,
      name: u.name,
      surname: u.surname,
      email: u.email,
      password: u.password,
      // El backend escribe con esto la fila de `user_account_scopes` de esta cuenta.
      scopes: u.scopes,
    }));
  }

  if (!payload.id) delete payload.id;

  return payload;
};
